import { useCallback, useEffect, useState } from 'react';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable } from '../../../components/ui/DataTable';
import { canAccessProviderAdmin } from './providerAdminAccess';

const TYPES = [
  'ORIGINATOR_TO_PROVIDER',
  'PROVIDER_TO_CUSTODIAN',
  'ORIGINATOR_TO_CUSTODIAN',
];

export function ConnectionsPage() {
  const { session } = useAuth();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({
    from_org_id: '',
    to_org_id: '',
    connection_type: 'ORIGINATOR_TO_PROVIDER',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const allowed = canAccessProviderAdmin(session);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [connections, orgPage] = await Promise.all([
        providerAdminApi.listConnections(),
        providerAdminApi.listOrganizations({ page: 1, page_size: 100 }),
      ]);
      setItems(Array.isArray(connections) ? connections : []);
      setOrgs(orgPage.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load connections');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
  }, [allowed, load]);

  if (!allowed) {
    return <p className="text-sm text-eorig-muted">Provider Admin access is required.</p>;
  }

  async function handleCreate(event) {
    event.preventDefault();
    setError(null);
    try {
      await providerAdminApi.createConnection(form);
      setForm((f) => ({ ...f, from_org_id: '', to_org_id: '' }));
      await load();
    } catch (err) {
      setError(err.message || 'Create failed');
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-eorig-muted">
        Stage 2 — connect existing organizations. Add an organization first if the list is empty.
      </p>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <form onSubmit={handleCreate} className="grid gap-3 rounded-2xl border border-eorig-border bg-white p-4 sm:grid-cols-4">
        <select
          required
          value={form.from_org_id}
          onChange={(e) => setForm((f) => ({ ...f, from_org_id: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          <option value="">From org</option>
          {orgs.map((org) => (
            <option key={org.id} value={org.id}>
              {org.code}
            </option>
          ))}
        </select>
        <select
          required
          value={form.to_org_id}
          onChange={(e) => setForm((f) => ({ ...f, to_org_id: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          <option value="">To org</option>
          {orgs.map((org) => (
            <option key={org.id} value={org.id}>
              {org.code}
            </option>
          ))}
        </select>
        <select
          value={form.connection_type}
          onChange={(e) => setForm((f) => ({ ...f, connection_type: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          {TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <button type="submit" className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white">
          Connect
        </button>
      </form>
      {loading ? (
        <p className="text-sm text-eorig-muted">Loading connections…</p>
      ) : (
        <DataTable
          columns={[
            { key: 'from_org_name', header: 'From' },
            { key: 'to_org_name', header: 'To' },
            { key: 'connection_type', header: 'Type' },
            { key: 'status', header: 'Status' },
          ]}
          rows={items}
          rowKey={(row) => row.id}
          emptyMessage="No connections yet."
        />
      )}
    </div>
  );
}
